import { inject, ref, unref, watch } from 'vue'
import uuid4 from 'uuid4'
import { useDebounceFn } from '@vueuse/core'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import { useI18n } from 'widget-components/utilities/i18n'

export const useAddressSuggestions = <T>(blockId: () => string, country: () => string | null | undefined) => {
    const { dispatchComponentAction } = inject(lazyBlockToolsInjectionKey, {
        requestBlockConfig: () => {},
        getLazyBlockProp: () => {},
        dispatchComponentAction: async () => {},
    } as LazyBlockToolsInject)

    const { locale } = useI18n()

    const searchQuery = ref<string | null>('')
    const suggestions = ref<T[]>([])
    const suggestionsLoading = ref(false)
    const searchCompleted = ref(false)

    let latestQuery: string | null = null
    // one search session: a new one starts after a pick and when the country changes
    let searchId = uuid4()

    watch(country, () => {
        searchId = uuid4()
    })

    const searchAddressSuggestions = useDebounceFn(async (query: string) => {
        latestQuery = query

        if (query.trim().length < 3) {
            suggestions.value = []
            suggestionsLoading.value = false
            return
        }

        suggestionsLoading.value = true
        const result = await dispatchComponentAction('address-autocomplete', {
            blockId: blockId(),
            query,
            searchId,
            country: country() || null,
            language: unref(locale) || null,
        })

        if (query !== latestQuery) {
            return
        }

        suggestions.value = (result as T[] | undefined) ?? []
        suggestionsLoading.value = false
        searchCompleted.value = true
    }, 500)

    const handleSearchInput = (query: string) => {
        searchCompleted.value = false
        searchAddressSuggestions(query)
    }

    const clearSearch = () => {
        searchQuery.value = ''
        suggestions.value = []
        latestQuery = null
        searchId = uuid4()
    }

    return { searchQuery, suggestions, suggestionsLoading, searchCompleted, handleSearchInput, clearSearch }
}
